import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { assign_product_nodes_path, products_product_nodes_path } from '@/lib/routes'
import { sendJson, UNASSIGNED, type Group, type ProductRow, type TreeProps } from './api'
import DisabledHint from './DisabledHint'
import MoveToSelect from './MoveToSelect'
import ProductTable from './ProductTable'

interface Props {
  groups: Group[]
  selectedId: string
  onTree: (tree: TreeProps) => void
}

const DEBOUNCE_MS = 300

export default function GroupProducts({ groups, selectedId, onTree }: Props) {
  const selected = groups.find((g) => g.id === selectedId)
  const hasSubGroups = Boolean(selected && !selected.is_leaf)
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [target, setTarget] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const requestId = useRef(0)

  useEffect(() => { setQuery(''); setRows([]); setChecked(new Set()); setErrors([]); setLoadError(false); setTarget('') }, [selectedId])
  useEffect(() => { setChecked(new Set()) }, [query])

  useEffect(() => {
    if (hasSubGroups) return
    const id = ++requestId.current
    setLoading(true)
    setLoadError(false)
    const timer = setTimeout(() => {
      const url = `${products_product_nodes_path()}?group_id=${encodeURIComponent(selectedId)}&q=${encodeURIComponent(query)}`
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => {
          if (!res.ok) throw new Error('load failed')
          return res.json()
        })
        .then((json: { products: ProductRow[] }) => { if (id === requestId.current) setRows(json.products) })
        .catch(() => { if (id === requestId.current) { setRows([]); setLoadError(true) } })
        .finally(() => { if (id === requestId.current) setLoading(false) })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [selectedId, query, reloadKey, hasSubGroups])

  async function assign(groupId: string | null) {
    const res = await sendJson<TreeProps>(assign_product_nodes_path(), 'PATCH', { group_id: groupId, product_ids: [...checked] })
    if (!res.ok || !res.data) return setErrors(res.errors)
    setErrors([])
    setChecked(new Set())
    onTree(res.data)
    setReloadKey((k) => k + 1)
  }

  function toggle(id: string) {
    const next = new Set(checked)
    if (!next.delete(id)) next.add(id)
    setChecked(next)
  }

  if (hasSubGroups) {
    return <p className="p-4 text-sm text-foreground">This group has sub-groups. Pick one of them to see its products.</p>
  }

  const none = checked.size === 0
  return (
    <div>
      <div className="border-b border-border px-3 py-2">
        <Input value={query} placeholder="Search products…" className="h-8 max-w-sm" onChange={(e) => setQuery(e.target.value)} />
      </div>
      <ProductTable
        rows={rows}
        checked={checked}
        onToggle={toggle}
        onToggleAll={() => setChecked(rows.every((r) => checked.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id)))}
      />
      {loadError && <p className="px-3 py-3 text-sm text-destructive">Could not load products. Please try again.</p>}
      {!loading && !loadError && rows.length === 0 && <p className="px-3 py-3 text-sm text-foreground">No products here.</p>}
      <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-border bg-card px-3 py-2">
        <MoveToSelect groups={groups} value={target} onChange={setTarget} />
        <DisabledHint reason={none ? 'Tick products first' : !target ? 'Pick a group to move to' : null}>
          <Button size="sm" className="h-8" disabled={none || !target} onClick={() => assign(target)}>Move</Button>
        </DisabledHint>
        {selectedId !== UNASSIGNED && (
          <DisabledHint reason={none ? 'Tick products first' : null}>
            <Button size="sm" variant="outline" className="h-8" disabled={none} onClick={() => assign(null)}>Remove from group</Button>
          </DisabledHint>
        )}
        {!none && <span className="text-sm text-foreground">{checked.size} selected</span>}
      </div>
      {errors.map((e) => <p key={e} className="px-3 pb-2 text-sm text-destructive">{e}</p>)}
    </div>
  )
}
